import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Copy, ListChecks, MessageSquare, Send, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { buildAppUrl } from "@/lib/url";
import { useSession } from "@/hooks/useSession";
import { TASK_DEPARTMENTS, TASK_PRIORITIES, TASK_STATUSES, type TaskPriority, type TaskStatus } from "./tasksConstants";
import { AssigneesMultiSelect } from "./AssigneesMultiSelect";
import {
  formatAbsoluteDateTime,
  formatCreatorLabel,
  formatRelativeTime,
  formatTaskCode,
  parseTaskRouteParam,
  statusDotClass,
} from "./tasksUi";

type SubtaskRow = {
  id: string;
  task_id: string;
  title: string;
  is_done: boolean;
  created_at: string;
  updated_at: string;
};

type CommentRow = {
  id: string;
  task_id: string;
  body: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  created_by_email: string | null;
};

export type TaskDetailRow = {
  id: string;
  task_number: number;
  title: string;
  assignees: string[];
  department: string;
  priority: TaskPriority;
  status: TaskStatus;
  created_at: string;
  updated_at: string;
  due_date: string | null;
  created_by_email: string | null;
  task_subtasks: SubtaskRow[];
};

type Props = {
  taskId: string;
  onClose?: () => void;
  onDeleted?: () => void;
  onUpdated?: (row: TaskDetailRow) => void;
  mode?: "auto" | "manual";
};

function sameStringArray(a: string[] | null | undefined, b: string[] | null | undefined) {
  const aa = (a ?? []).slice().sort();
  const bb = (b ?? []).slice().sort();
  if (aa.length !== bb.length) return false;
  for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false;
  return true;
}

function sortSubtasks(list: SubtaskRow[]) {
  return [...list].sort((a, b) => {
    if (a.is_done !== b.is_done) return a.is_done ? 1 : -1;
    return a.updated_at.localeCompare(b.updated_at);
  });
}

export function TasksTaskDetail({ taskId, onClose, onDeleted, onUpdated, mode = "auto" }: Props) {
  const { session } = useSession();
  const myEmail = session?.user?.email ?? null;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [row, setRow] = useState<TaskDetailRow | null>(null);
  const [subtaskTitle, setSubtaskTitle] = useState("");
  const [draftTitle, setDraftTitle] = useState("");
  const [draftAssignees, setDraftAssignees] = useState<string[]>([]);
  const [draftPriority, setDraftPriority] = useState<TaskPriority>("normal");
  const [draftStatus, setDraftStatus] = useState<TaskStatus>("to-do");
  const [draftDepartment, setDraftDepartment] = useState<string>(TASK_DEPARTMENTS[0]);
  const [draftDueDate, setDraftDueDate] = useState("");

  const [comments, setComments] = useState<CommentRow[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [postingComment, setPostingComment] = useState(false);

  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
    };
  }, []);

  const activeSubtasks = useMemo(() => (row?.task_subtasks ?? []).filter((s) => !s.is_done), [row]);
  const doneSubtasks = useMemo(() => (row?.task_subtasks ?? []).filter((s) => s.is_done), [row]);

  function normDue(s: string | null | undefined) {
    return s?.trim() || null;
  }

  const isDirty =
    mode === "manual" &&
    !!row &&
    (draftTitle.trim() !== row.title ||
      !sameStringArray(draftAssignees, row.assignees) ||
      draftPriority !== row.priority ||
      draftStatus !== row.status ||
      draftDepartment !== row.department ||
      normDue(draftDueDate) !== normDue(row.due_date));

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      const cols =
        "id,task_number,title,assignees,department,priority,status,created_at,updated_at,due_date,created_by_email,task_subtasks(id,task_id,title,is_done,created_at,updated_at)";
      let query = supabase.from("tasks").select(cols);
      const parsed = parseTaskRouteParam(taskId);
      if (parsed?.by === "task_number") {
        query = query.eq("task_number", parsed.n);
      } else if (parsed?.by === "id") {
        query = query.eq("id", parsed.id);
      } else {
        query = query.eq("id", taskId);
      }

      const { data, error } = await query.single();

      if (cancelled) return;

      if (error) {
        setError(error.message);
        setRow(null);
      } else {
        const d = data as TaskDetailRow;
        d.task_subtasks = sortSubtasks(d.task_subtasks ?? []);
        setRow(d);
        setDraftTitle(d.title ?? "");
        setDraftAssignees(d.assignees ?? []);
        setDraftPriority(d.priority ?? "normal");
        setDraftStatus(d.status);
        setDraftDepartment(d.department);
        setDraftDueDate(d.due_date ?? "");
      }
      setLoading(false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  useEffect(() => {
    const id = row?.id;
    if (!id) {
      setComments([]);
      return;
    }

    let cancelled = false;
    async function load() {
      setCommentsLoading(true);
      setCommentsError(null);

      const { data, error } = await supabase
        .from("task_comments")
        .select("id,task_id,body,created_at,updated_at,created_by,created_by_email")
        .eq("task_id", id)
        .order("created_at", { ascending: true });

      if (cancelled) return;
      if (error) {
        setCommentsError(error.message);
        setComments([]);
      } else {
        setComments((data ?? []) as CommentRow[]);
      }
      setCommentsLoading(false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [row?.id]);

  async function postComment() {
    if (!row) return;
    const body = commentDraft.trim();
    if (!body) return;

    setPostingComment(true);
    setCommentsError(null);

    const { data, error } = await supabase
      .from("task_comments")
      .insert({ task_id: row.id, body })
      .select("id,task_id,body,created_at,updated_at,created_by,created_by_email")
      .single();

    setPostingComment(false);

    if (error) {
      setCommentsError(error.message);
      return;
    }

    setComments((cur) => [...cur, data as CommentRow]);
    setCommentDraft("");
  }

  async function deleteComment(commentId: string) {
    if (!row) return;
    const ok = confirm("Delete this comment?");
    if (!ok) return;

    setCommentsError(null);

    const prev = comments;
    setComments((cur) => cur.filter((c) => c.id !== commentId));

    const { error } = await supabase.from("task_comments").delete().eq("id", commentId);
    if (error) {
      setComments(prev);
      setCommentsError(error.message);
    }
  }

  async function patchTask(
    patch: Partial<Pick<TaskDetailRow, "title" | "assignees" | "department" | "priority" | "status" | "due_date">>
  ): Promise<TaskDetailRow | null> {
    if (!row) return null;
    setSaving(true);
    setError(null);

    const prev = row;
    setRow({ ...row, ...patch, updated_at: new Date().toISOString() } as TaskDetailRow);

    const { data, error } = await supabase
      .from("tasks")
      .update(patch)
      .eq("id", row.id)
      .select(
        "id,task_number,title,assignees,department,priority,status,created_at,updated_at,due_date,created_by_email,task_subtasks(id,task_id,title,is_done,created_at,updated_at)"
      )
      .single();

    setSaving(false);

    if (error) {
      setRow(prev);
      setError(error.message);
      return null;
    }

    const next = data as TaskDetailRow;
    next.task_subtasks = sortSubtasks(next.task_subtasks ?? []);
    setRow(next);
    onUpdated?.(next);
    return next;
  }

  async function addSubtask() {
    if (!row) return;
    const title = subtaskTitle.trim();
    if (!title) return;
    setError(null);

    const { data, error } = await supabase
      .from("task_subtasks")
      .insert({ task_id: row.id, title })
      .select("id,task_id,title,is_done,created_at,updated_at")
      .single();

    if (error) {
      setError(error.message);
      return;
    }

    setSubtaskTitle("");
    const next = { ...row, task_subtasks: sortSubtasks([...(row.task_subtasks ?? []), data as SubtaskRow]) };
    setRow(next);
    onUpdated?.(next);
  }

  async function toggleSubtask(subtaskId: string, is_done: boolean) {
    if (!row) return;
    setError(null);

    const prev = row;
    const optimistic = {
      ...row,
      task_subtasks: sortSubtasks(
        (row.task_subtasks ?? []).map((s) =>
          s.id === subtaskId ? { ...s, is_done, updated_at: new Date().toISOString() } : s
        )
      ),
    };
    setRow(optimistic);

    const { error } = await supabase.from("task_subtasks").update({ is_done }).eq("id", subtaskId);
    if (error) {
      setRow(prev);
      setError(error.message);
      return;
    }
    onUpdated?.(optimistic);
  }

  async function deleteSubtask(subtaskId: string) {
    if (!row) return;
    setError(null);

    const prev = row;
    const optimistic = { ...row, task_subtasks: (row.task_subtasks ?? []).filter((s) => s.id !== subtaskId) };
    setRow(optimistic);

    const { error } = await supabase.from("task_subtasks").delete().eq("id", subtaskId);
    if (error) {
      setRow(prev);
      setError(error.message);
      return;
    }
    onUpdated?.(optimistic);
  }

  async function deleteTask() {
    if (!row) return;
    const ok = confirm("Delete this task and its subtasks?");
    if (!ok) return;

    setSaving(true);
    setError(null);
    const { error } = await supabase.from("tasks").delete().eq("id", row.id);
    setSaving(false);

    if (error) {
      setError(error.message);
      return;
    }
    onDeleted?.();
  }

  async function saveManual() {
    if (!row) return;
    const next = await patchTask({
      title: draftTitle.trim(),
      assignees: draftAssignees,
      priority: draftPriority,
      status: draftStatus,
      department: draftDepartment,
      due_date: normDue(draftDueDate),
    });
    if (next) {
      setDraftTitle(next.title ?? "");
      setDraftAssignees(next.assignees ?? []);
      setDraftPriority(next.priority ?? "normal");
      setDraftStatus(next.status);
      setDraftDepartment(next.department);
      setDraftDueDate(next.due_date ?? "");
    }
  }

  if (loading) {
    return <div className="rounded-xl bg-brand-gray/20 p-6 text-sm text-brand-blue/70">Loading…</div>;
  }

  if (!row) {
    return (
      <div className="rounded-xl bg-red-50 p-4 text-sm font-semibold text-red-700">
        {error ?? "Task not found."}
      </div>
    );
  }

  const shareHref = `/tasks/${formatTaskCode(row.task_number)}`;
  const creatorLabel = formatCreatorLabel(row.created_by_email);

  return (
    <div className="space-y-4">
      {error ? (
        <p className="rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{error}</p>
      ) : null}

      <div>
        <label className="block text-xs font-bold uppercase tracking-wide text-brand-blue/60">
          Title
        </label>
        <textarea
          value={mode === "manual" ? draftTitle : row.title}
          onChange={(e) => {
            const v = e.target.value;
            if (mode === "manual") setDraftTitle(v);
            else setRow({ ...row, title: v });
          }}
          onBlur={mode === "manual" ? undefined : () => void patchTask({ title: row.title.trim() })}
          rows={2}
          spellCheck={true}
          className="mt-2 w-full min-h-[5rem] resize-y rounded-2xl bg-white px-4 py-3 font-display text-lg font-bold leading-snug text-brand-blue shadow-sm outline-none ring-2 ring-brand-gray/70 focus:ring-brand-maroon break-words"
          placeholder="Task title…"
        />
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-brand-blue/60">
          <span className="font-mono font-bold text-brand-blue/75">{formatTaskCode(row.task_number)}</span>
          {creatorLabel ? (
            <span>
              Created by <span className="font-semibold text-brand-blue/80">{creatorLabel}</span>
            </span>
          ) : null}
          <span className="ml-auto text-right text-brand-blue/55">
            {mode === "manual" ? "Edit fields then tap Save." : "Edit and click outside to save."}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
        <div className="md:col-span-4">
          <label className="block text-sm font-semibold">Deadline</label>
          <input
            type="date"
            value={mode === "manual" ? draftDueDate : row.due_date ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              if (mode === "manual") setDraftDueDate(v);
              else void patchTask({ due_date: v.trim() ? v.trim() : null });
            }}
            className="mt-2 w-full rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
          />
          <p className="mt-1 text-xs text-brand-blue/55">Optional</p>
        </div>

        <div className="md:col-span-4">
          <label className="block text-sm font-semibold">Priority</label>
          <select
            value={mode === "manual" ? draftPriority : row.priority}
            onChange={(e) => {
              const v = e.target.value as TaskPriority;
              if (mode === "manual") setDraftPriority(v);
              else void patchTask({ priority: v });
            }}
            className="mt-2 w-full rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
          >
            {TASK_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p === "high" ? "High" : "Normal"}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-4">
          <label className="block text-sm font-semibold">Assigned</label>
          <AssigneesMultiSelect
            value={mode === "manual" ? draftAssignees : row.assignees ?? []}
            onChange={(next) => {
              if (mode === "manual") setDraftAssignees(next);
              else void patchTask({ assignees: next });
            }}
          />
        </div>

        <div className="md:col-span-4">
          <label className="block text-sm font-semibold">Status</label>
          <div className="relative mt-2">
            <span
              className={[
                "pointer-events-none absolute left-3 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full ring-2 ring-white/80",
                statusDotClass(mode === "manual" ? draftStatus : row.status),
              ].join(" ")}
              aria-hidden="true"
            />
            <select
              value={mode === "manual" ? draftStatus : row.status}
              onChange={(e) => {
                const v = e.target.value as TaskStatus;
                if (mode === "manual") setDraftStatus(v);
                else void patchTask({ status: v });
              }}
              className="w-full rounded-xl bg-brand-gray/40 py-2.5 pl-9 pr-3 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
            >
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="md:col-span-4">
          <label className="block text-sm font-semibold">Department</label>
          <select
            value={mode === "manual" ? draftDepartment : row.department}
            onChange={(e) => {
              const v = e.target.value;
              if (mode === "manual") setDraftDepartment(v);
              else void patchTask({ department: v });
            }}
            className="mt-2 w-full rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
          >
            {TASK_DEPARTMENTS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      </div>

      <section className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200/80">
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-slate-600" />
          <p className="text-sm font-bold uppercase tracking-wide text-slate-700">Subtasks</p>
          {activeSubtasks.length + doneSubtasks.length ? (
            <span className="ml-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-slate-700 ring-1 ring-slate-200">
              {doneSubtasks.length}/{activeSubtasks.length + doneSubtasks.length}
            </span>
          ) : null}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void addSubtask();
          }}
          className="mt-3 flex items-center gap-2"
        >
          <input
            value={subtaskTitle}
            onChange={(e) => setSubtaskTitle(e.target.value)}
            placeholder="Add a subtask…"
            className="w-full rounded-xl bg-white px-3 py-2.5 text-sm outline-none ring-2 ring-slate-200 focus:ring-brand-maroon"
          />
          <button
            type="submit"
            className="shrink-0 rounded-full bg-brand-blue px-4 py-2 text-xs font-bold uppercase tracking-wide text-brand-gray"
          >
            Add
          </button>
        </form>

        <div className="mt-3 space-y-2">
          {activeSubtasks.length === 0 ? (
            <p className="text-sm text-slate-500">No subtasks yet.</p>
          ) : (
            activeSubtasks.map((s) => (
              <div
                key={s.id}
                className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200"
              >
                <input
                  type="checkbox"
                  checked={s.is_done}
                  onChange={(e) => void toggleSubtask(s.id, e.target.checked)}
                />
                <p className="min-w-0 flex-1 truncate text-sm font-semibold">{s.title}</p>
                <button
                  type="button"
                  onClick={() => void deleteSubtask(s.id)}
                  className="rounded-full px-2 py-1 text-xs font-bold text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                >
                  Delete
                </button>
              </div>
            ))
          )}
        </div>

        {doneSubtasks.length ? (
          <details className="mt-3">
            <summary className="cursor-pointer select-none text-xs font-bold uppercase tracking-wide text-slate-500">
              Done subtasks ({doneSubtasks.length})
            </summary>
            <div className="mt-2 space-y-2">
              {doneSubtasks.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200"
                >
                  <input
                    type="checkbox"
                    checked={s.is_done}
                    onChange={(e) => void toggleSubtask(s.id, e.target.checked)}
                  />
                  <p className="min-w-0 flex-1 truncate text-sm text-slate-500 line-through">{s.title}</p>
                  <button
                    type="button"
                    onClick={() => void deleteSubtask(s.id)}
                    className="rounded-full px-2 py-1 text-xs font-bold text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </details>
        ) : null}
      </section>

      <section className="rounded-2xl bg-amber-50/70 p-4 ring-1 ring-amber-200/70">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-amber-800" />
          <p className="text-sm font-bold uppercase tracking-wide text-amber-900">Comments</p>
          {comments.length ? (
            <span className="ml-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-amber-900 ring-1 ring-amber-200">
              {comments.length}
            </span>
          ) : null}
          {commentsLoading ? (
            <span className="ml-auto text-xs font-semibold text-amber-900/60">Loading…</span>
          ) : null}
        </div>

        {commentsError ? (
          <p className="mt-2 rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {commentsError}
          </p>
        ) : null}

        <div className="mt-3 space-y-2">
          {!commentsLoading && comments.length === 0 ? (
            <p className="text-sm text-amber-900/70">No comments yet. Start the discussion.</p>
          ) : (
            comments.map((c) => {
              const author = formatCreatorLabel(c.created_by_email) ?? "Member";
              const isMine =
                !!myEmail && !!c.created_by_email && myEmail.toLowerCase() === c.created_by_email.toLowerCase();
              return (
                <div
                  key={c.id}
                  className={[
                    "rounded-xl bg-white px-3 py-2.5 shadow-sm ring-1 ring-amber-200/60",
                    isMine ? "border-l-4 border-brand-maroon" : "border-l-4 border-amber-300",
                  ].join(" ")}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="min-w-0 truncate text-xs font-bold text-brand-blue">
                      {author}
                      {isMine ? (
                        <span className="ml-1 text-[10px] font-semibold text-brand-maroon">(you)</span>
                      ) : null}
                    </p>
                    <p
                      className="shrink-0 text-[11px] font-semibold text-brand-blue/55"
                      title={formatAbsoluteDateTime(c.created_at)}
                    >
                      {formatRelativeTime(c.created_at)}
                    </p>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-snug text-brand-blue/90">
                    {c.body}
                  </p>
                  {isMine ? (
                    <div className="mt-1.5 flex justify-end">
                      <button
                        type="button"
                        onClick={() => void deleteComment(c.id)}
                        className="rounded-full px-2 py-1 text-[11px] font-bold text-brand-blue/55 hover:bg-amber-100 hover:text-brand-maroon"
                      >
                        Delete
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void postComment();
          }}
          className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end"
        >
          <textarea
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                void postComment();
              }
            }}
            rows={2}
            placeholder="Add a comment… (Cmd/Ctrl+Enter to post)"
            className="min-h-[3.25rem] flex-1 resize-y rounded-xl bg-white px-3 py-2.5 text-sm outline-none ring-2 ring-amber-200/80 focus:ring-brand-maroon"
          />
          <button
            type="submit"
            disabled={postingComment || !commentDraft.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-maroon px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-white disabled:opacity-50 sm:w-auto"
          >
            <Send className="h-4 w-4" />
            {postingComment ? "Posting…" : "Post"}
          </button>
        </form>
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={() => void deleteTask()}
          title="Delete task"
          aria-label="Delete task"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-700 ring-1 ring-red-200 transition-colors hover:bg-red-100 hover:text-red-800 disabled:opacity-60"
        >
          <Trash2 className="h-4 w-4" />
        </button>

        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            title="Copy link to this task"
            onClick={() => {
              void navigator.clipboard?.writeText(buildAppUrl(shareHref));
              setCopied(true);
              if (copyTimer.current) window.clearTimeout(copyTimer.current);
              copyTimer.current = window.setTimeout(() => setCopied(false), 1500);
            }}
            className={[
              "inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide ring-1 transition-colors",
              copied
                ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
                : "bg-white text-brand-blue ring-black/10 hover:bg-brand-gray/30",
            ].join(" ")}
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            <span className="font-mono normal-case tracking-tight">{formatTaskCode(row.task_number)}</span>
            <span className="opacity-60">·</span>
            <span>{copied ? "Copied" : "Copy task"}</span>
          </button>

          {mode === "manual" ? (
            <button
              type="button"
              disabled={saving || !isDirty}
              onClick={() => void saveManual()}
              className="inline-flex items-center justify-center rounded-full bg-brand-blue px-5 py-2.5 text-sm font-bold text-brand-gray disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          ) : null}

          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-full bg-brand-blue px-5 py-2.5 text-sm font-bold text-brand-gray"
            >
              Done
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

